import { useMemo } from 'react';
import {
  useFormState,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
  Badge,
  BadgeVariants,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { FieldSection } from '@/components/common';
import { formatTextOrDash } from '@/lib/format/text';
import { formatDateOrDash } from '@/lib/format/date';
import { formatQuantityOrDash } from '@/lib/format/number';
import {
  feedstockCategoryLabel,
  allocationPreferenceLabel,
  FACILITY_KINDS,
  parseFacilityValue,
} from '@/config/formOptions';
import { verificationStatusLabel, buildVerificationStatusVariants } from '@/config/statuses';
import { VERIFICATION_STATUS_TONE } from '@/config/statusTones';
import {
  GHG_DEFAULT_VALUE,
  MULTISITE_TRANSFER_VALUE,
} from '../../constants/validateDeclaration.constants';
import { useDeclarationQuantityColumns } from '../../hooks';
import styles from './ValidateDeclarationSummary.module.scss';

const dash = (value) => formatTextOrDash(value);
const date = (value) => formatDateOrDash(value);

const labelFor = (options, value) =>
  dash(options.find((option) => String(option.value) === String(value))?.label);

const GHG_EMISSION_UNIT = 'g CO2 e / MJ';
const withUnit = (value, unit) => (value ? `${value} ${unit}` : dash());
const withPercent = (value) => (value ? `${value} %` : dash());

// SummaryCardShell — one FieldCard: a title bar over 1+ FieldSection columns, per ContractOverviewTabView.
const SummaryCardShell = ({ title, children }) => (
  <div className={styles.card}>
    <div className={styles.cardHeader}>
      <Typography
        variant={TypographyVariants.bodySm}
        weight={TypographyWeights.semibold}
        color={TypographyColors.primary}
      >
        {title}
      </Typography>
    </div>
    <div className={styles.cardBody}>{children}</div>
  </div>
);

/**
 * Props:
 *   formOptions  useValidateDeclarationFormOptions() result, to resolve id fields to names
 */
export const ValidateDeclarationSummary = ({ formOptions }) => {
  const t = useTranslations();
  const { values } = useFormState();

  const yesNo = (value, yesValue) =>
    value ? (value === yesValue ? t('COMMON_YES') : t('COMMON_NO')) : dash();

  const materialName = dash(
    formOptions.materials.find((material) => String(material.id) === String(values.materialId))
      ?.name,
  );
  const terminalName = dash(
    formOptions.sites
      .flatMap((site) => site.terminals ?? [])
      .find((terminal) => String(terminal.id) === String(values.terminalId))?.name,
  );
  // A back-to-back account has no Terminal field in step 1, so it stays out of the summary too.
  const isBackToBack = parseFacilityValue(values.facilityId).kind === FACILITY_KINDS.backToBack;

  const { declarationUnit, workingUnit, trackingUnit, columns } = useDeclarationQuantityColumns({
    materials: formOptions.materials,
    materialId: values.materialId,
    quantity: values.quantity,
  });

  const verificationVariants = useMemo(
    () => buildVerificationStatusVariants(t, VERIFICATION_STATUS_TONE),
    [t],
  );
  const verificationLabel = verificationStatusLabel(
    values.markedAsVerified
      ? 'MANUALLY_VERIFIED'
      : values.certificateLiveVerified || values.certificateVerificationStatus === 'VERIFIED'
        ? 'VERIFIED'
        : 'REJECTED',
  );
  const isDefaultGhgNo = values.defaultGhgValueApplied === GHG_DEFAULT_VALUE.no;

  return (
    <div className={styles.summary}>
      <SummaryCardShell title={t('DECLARATIONS_VALIDATE_SUMMARY_GENERAL_INFO')}>
        <FieldSection
          heading={t('SECTION_BASIC_INFORMATION')}
          fields={[
            [
              { label: t('DECLARATIONS_COL_REFERENCE_ID'), value: dash(values.idNumber) },
              { label: t('FIELD_MATERIAL_NAME'), value: materialName },
            ],
            isBackToBack
              ? {
                  label: t('FORM_RECEIVED_AT_FACILITY'),
                  value: labelFor(formOptions.facilityOptions, values.facilityId),
                }
              : [
                  {
                    label: t('FORM_RECEIVED_AT_FACILITY'),
                    value: labelFor(formOptions.facilityOptions, values.facilityId),
                  },
                  { label: t('FIELD_TERMINAL'), value: terminalName },
                ],
            [
              {
                label: t('FORM_MULTI_SITE_TRANSFER'),
                value: yesNo(values.multisiteTransfer, MULTISITE_TRANSFER_VALUE.yes),
              },
              {
                label: t('FORM_SENT_BY_SUPPLIER'),
                value: labelFor(formOptions.supplierOptions, values.supplierId),
              },
            ],
            [
              { label: t('FIELD_DATE_OF_DISCHARGE'), value: date(values.dateOfDischarge) },
              { label: t('FORM_VESSEL_NAME'), value: dash(values.vessel) },
            ],
          ]}
        />
        <FieldSection
          heading={t('SECTION_ALLOCATION_RULES')}
          fields={[
            {
              label: t('FORM_FEEDSTOCK_CATEGORY'),
              value: dash(feedstockCategoryLabel(values.feedstockCategory)),
            },
            {
              label: t('FORM_ALLOCATION_PREFERENCE'),
              value: dash(
                (values.allocationPreference ?? []).map(allocationPreferenceLabel).join(', '),
              ),
            },
          ]}
        />
      </SummaryCardShell>

      <SummaryCardShell title={t('DECLARATIONS_VALIDATE_SUMMARY_DECLARATION_DETAILS')}>
        <FieldSection
          heading={t('SECTION_SUSTAINABILITY_DECLARATION_DETAILS')}
          fields={[
            { label: t('FIELD_DECLARATION_NO'), value: dash(values.declarationNo) },
            [
              {
                label: t('DECLARATIONS_COL_DATE_OF_ISSUANCE'),
                value: date(values.dateOfIssuance),
              },
              { label: t('FIELD_DATE_OF_DISPATCH'), value: date(values.dateOfDispatch) },
            ],
            [
              {
                label: t('FIELD_SUPPLIER_CERTIFICATE_NUMBER'),
                value: dash(values.supplierCertificateNumber),
              },
              {
                label: t('FIELD_DECLARATION_VERIFICATION_STATUS'),
                value: verificationLabel ? (
                  <Badge
                    variant={verificationVariants[verificationLabel] ?? BadgeVariants.default}
                    withDot
                  >
                    {verificationLabel}
                  </Badge>
                ) : (
                  dash()
                ),
              },
            ],
            // TODO: no note field exists on the declaration form/backend yet.
            { label: t('FIELD_NOTE'), value: dash() },
          ]}
        />
      </SummaryCardShell>

      <SummaryCardShell title={t('DECLARATIONS_VALIDATE_SUMMARY_MATERIAL_DETAILS')}>
        <FieldSection
          heading={t('SECTION_GENERAL_INFORMATION')}
          fields={[
            {
              label: t('FIELD_TYPE_OF_PRODUCT'),
              value: labelFor(formOptions.productTypeOptions, values.typeOfProduct),
            },
            {
              label: t('FIELD_TYPE_OF_RAW_MATERIAL'),
              value: labelFor(formOptions.rawMaterialTypeOptions, values.typeOfRawMaterial),
            },
            {
              label: t('FORM_ADDITIONAL_INFORMATION'),
              value: dash(values.additionalInformation),
            },
            {
              label: t('FIELD_COUNTRY_OF_ORIGIN'),
              value: labelFor(formOptions.countryOptions, values.countryOfOrigin),
            },
            [
              { label: t('FIELD_QUANTITY'), value: dash(values.quantity) },
              { label: t('FIELD_UNIT'), value: dash(declarationUnit) },
            ],
            ...(workingUnit || trackingUnit
              ? [
                  [
                    workingUnit && {
                      label: t('SECTION_QUANTITY_IN_UNIT', { unit: workingUnit }),
                      value: formatQuantityOrDash(columns.workingQuantity, workingUnit),
                    },
                    trackingUnit && {
                      label: t('SECTION_QUANTITY_IN_UNIT', { unit: trackingUnit }),
                      value: formatQuantityOrDash(columns.trackingQuantity, trackingUnit),
                    },
                  ].filter(Boolean),
                ]
              : []),
            {
              label: t('FIELD_EU_RED_COMPLIANT_MATERIAL'),
              value: labelFor(formOptions.yesNoOptions, values.euRedCompliantMaterial),
            },
            {
              label: t('FORM_ISCC_COMPLIANT_MATERIAL'),
              value: labelFor(formOptions.yesNoOptions, values.isccCompliantMaterial),
            },
            {
              label: t('FORM_CHAIN_OF_CUSTODY_OPTION'),
              value: labelFor(formOptions.chainOfCustodyOptions, values.chainOfCustodyOption),
            },
            {
              label: t('FIELD_COUNTRY_OF_BIOFUEL_PRODUCTION'),
              value: labelFor(formOptions.countryOptions, values.countryOfBiofuelProduction),
            },
            {
              label: t('FIELD_START_DATE_OF_BIOFUEL_PRODUCTION'),
              value: date(values.startDateOfBiofuelProduction),
            },
            {
              label: t('FORM_START_DATE_OF_BIOLIQUID_USE'),
              value: date(values.startDateOfBioliquidUse),
            },
          ]}
        />
        <FieldSection
          heading={t('SECTION_CERTIFICATION_SCOPE')}
          fields={[
            {
              label: t('FIELD_SUSTAINABILITY_CRITERIA_COMPLIANCE'),
              value: labelFor(formOptions.yesNoOptions, values.sustainabilityCriteriaCompliance),
            },
            {
              label: t('FORM_INTERMEDIATE_CROP'),
              value: labelFor(formOptions.yesNoOptions, values.intermediateCrop),
            },
            {
              label: t('FORM_LOW_ILUC_RISK_FEEDSTOCK'),
              value: labelFor(formOptions.yesNoOptions, values.lowIlucRiskFeedstock),
            },
            {
              label: t('FIELD_WASTE_OR_RESIDUE_DEFINITION'),
              value: labelFor(formOptions.yesNoOptions, values.wasteOrResidueDefinition),
            },
            {
              label: t('FORM_WASTE_OR_BY_PRODUCT_PERMIT_NUMBER'),
              value: dash(values.wasteOrByProductPermitNumber),
            },
            {
              label: t('FIELD_PRODUCTION_SUPPORT_RECEIVED'),
              value: labelFor(formOptions.yesNoOptions, values.productionSupportReceived),
            },
            {
              label: t('FORM_SUPPORT_NATURE_AND_SCHEME'),
              value: dash(values.supportNatureAndScheme),
            },
          ]}
        />
      </SummaryCardShell>

      <SummaryCardShell title={t('DECLARATIONS_VALIDATE_SUMMARY_EMISSION_DETAILS')}>
        <FieldSection
          heading={t('SECTION_GHG_EMISSION_DETAILS')}
          fields={[
            {
              label: t('FIELD_DEFAULT_GHG_VALUE_APPLIED'),
              value: yesNo(values.defaultGhgValueApplied, GHG_DEFAULT_VALUE.yes),
            },
            {
              label: t('FIELD_TOTAL_GHG_EMISSIONS'),
              value: withUnit(values.totalGhgEmissions, GHG_EMISSION_UNIT),
            },
            ...(isDefaultGhgNo
              ? [
                  [
                    {
                      label: t('FIELD_EEC_EMISSIONS'),
                      value: withUnit(values.eec, GHG_EMISSION_UNIT),
                    },
                    {
                      label: t('FIELD_EL_EMISSIONS'),
                      value: withUnit(values.el, GHG_EMISSION_UNIT),
                    },
                  ],
                  [
                    {
                      label: t('FIELD_EP_EMISSIONS'),
                      value: withUnit(values.ep, GHG_EMISSION_UNIT),
                    },
                    {
                      label: t('FIELD_ETD_EMISSIONS'),
                      value: withUnit(values.etd, GHG_EMISSION_UNIT),
                    },
                  ],
                  [
                    {
                      label: t('FIELD_EU_EMISSIONS'),
                      value: withUnit(values.eu, GHG_EMISSION_UNIT),
                    },
                    {
                      label: t('FIELD_ESCA_EMISSIONS'),
                      value: withUnit(values.escaValue, GHG_EMISSION_UNIT),
                    },
                  ],
                  [
                    {
                      label: t('FIELD_ECCS_EMISSIONS'),
                      value: withUnit(values.eccs, GHG_EMISSION_UNIT),
                    },
                    {
                      label: t('FIELD_ECCR_EMISSIONS'),
                      value: withUnit(values.eccr, GHG_EMISSION_UNIT),
                    },
                  ],
                ]
              : []),
            {
              label: t('FORM_ALLOCATED_HEAT'),
              value: withUnit(values.allocatedHeat, GHG_EMISSION_UNIT),
            },
            {
              label: t('FORM_ALLOCATED_ELECTRICITY'),
              value: withUnit(values.allocatedElectricity, GHG_EMISSION_UNIT),
            },
            {
              label: t('FORM_ESCA_BONUS_CAP'),
              value: labelFor(formOptions.escaBonusCapOptions, values.escaBonusCap),
            },
            { label: t('FORM_NUTS2_AREA'), value: dash(values.nuts2Area) },
          ]}
        />
        <FieldSection
          heading={t('SECTION_GHG_EMISSION_SAVING')}
          fields={[
            {
              label: t('FORM_BIOFUELS_TRANSPORT_SAVING'),
              value: withPercent(values.biofuelsForTransportSaving),
            },
            {
              label: t('FORM_BIOLIQUIDS_ELECTRICITY_SAVING'),
              value: withPercent(values.bioliquidsElectricitySaving),
            },
            {
              label: t('FORM_BIOMASS_OUTERMOST_REGIONS_SAVING'),
              value: withPercent(values.biomassOutermostRegionsSaving),
            },
            {
              label: t('FORM_BIOLIQUIDS_HEAT_SAVING'),
              value: withPercent(values.bioliquidsHeatSaving),
            },
            {
              label: t('FORM_BIOMASS_COAL_SUBSTITUTION_SAVING'),
              value: withPercent(values.biomassCoalSubstitutionSaving),
            },
          ]}
        />
        <FieldSection
          heading={t('SECTION_VOLUNTARY_ADD_ONS')}
          fields={[
            {
              label: t('FORM_FOOD_SECURITY_STANDARD'),
              value: labelFor(formOptions.yesNoOptions, values.foodSecurityStandard),
            },
            {
              label: t('FORM_EU_DEFORESTATION_REGULATION'),
              value: labelFor(formOptions.yesNoOptions, values.euDeforestationRegulation),
            },
            {
              label: t('FORM_NON_GMO_FOOD_FEED'),
              value: labelFor(formOptions.yesNoOptions, values.nonGmoFoodFeed),
            },
            {
              label: t('FORM_NON_GMO_TECHNICAL_MARKETS'),
              value: labelFor(formOptions.yesNoOptions, values.nonGmoTechnicalMarkets),
            },
          ]}
        />
      </SummaryCardShell>
    </div>
  );
};
